import { useCallback, useEffect, useState } from "react";
import { Pagination, PaginationItem, TextField } from "@mui/material";
import { Form } from "react-bootstrap";
import {
  emptyString,
  toastTypes,
  KEYBOARD_KEYS,
  PAGINATION_INDEXING,
  PAGINATION_EVENTS,
  PAGINATION_DISPLAY,
  PAGINATION_INPUT,
  PAGINATION_MESSAGES,
  PAGINATION_ITEM_TYPES,
  PAGINATION_PAGE_SIZES,
  commonUnitTestsIdsConstants as TEST_IDS,
} from "../../../../constants";
import { customToast } from "../../../utility";
import {
  paginatorCellStyle,
  pageNumberInputSx,
  paginationSx,
} from "./paginator.styles";

const PageNumberInput = ({ value, onChange, onKeyDown, totalPages }) => (
  <TextField
    value={value}
    onChange={onChange}
    onWheel={(e) => e.target.blur()}
    onFocus={(e) => e.stopPropagation()}
    onClick={(e) => e.stopPropagation()}
    onKeyDown={onKeyDown}
    size={PAGINATION_INPUT.CONTROL_SIZE}
    type={PAGINATION_INPUT.FIELD_TYPE}
    slotProps={{
      htmlInput: {
        min: PAGINATION_INDEXING.FIRST_PAGE_NUMBER,
        max: totalPages,
        "data-testid": TEST_IDS.PAGE_NUMBER_INPUT,
      },
    }}
    autoFocus
    sx={pageNumberInputSx}
  />
);

export const Paginator = (props) => {
  const { paginationCustomClass = emptyString } = props;
  const [isInputVisible, setIsInputVisible] = useState(false);
  const [pageValue, setPageValue] = useState(props.activePageNumber);

  const maxPageWarning = `${PAGINATION_MESSAGES.MAXIMUM_PAGE_WARNING_PREFIX} ${props.totalPages}`;

  const closeInput = useCallback(() => {
    setIsInputVisible(false);
    setPageValue(props.activePageNumber);
  }, [props.activePageNumber]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    if (value > props.totalPages) {
      customToast(maxPageWarning, toastTypes.WARNING);
      return;
    } else if (value !== emptyString && value < PAGINATION_INDEXING.FIRST_PAGE_NUMBER) {
      customToast(PAGINATION_MESSAGES.MINIMUM_PAGE_WARNING, toastTypes.WARNING);
      return;
    }

    if (PAGINATION_INPUT.DIGITS_ONLY_PATTERN.test(value)) {
      // Allows only whole numbers (digits only)
      setPageValue(value);
    }
  };

  const handlePressEnter = (e) => {
    if (e.key === KEYBOARD_KEYS.ENTER) {
      const newPage = parseInt(pageValue, PAGINATION_INPUT.DECIMAL_RADIX);

      if (!pageValue) {
        closeInput();
      } else {
        setPageValue(newPage);
        props.activatePage(newPage);
      }
    }
  };

  const activateCurrentPageSize = (e) => {
    if (props.currentPageSize) {
      props.activateCurrentPageSize(e.target.value);
    }
  };

  useEffect(() => {
    document.addEventListener(PAGINATION_EVENTS.DOCUMENT_CLICK, closeInput);
    return () =>
      document.removeEventListener(PAGINATION_EVENTS.DOCUMENT_CLICK, closeInput);
  }, [closeInput]);

  const renderPageInfo = () => (
    <small>
      {props.isSelectedRows
        ? props.selectedRows + PAGINATION_MESSAGES.SELECTED_ROWS_SUFFIX
        : emptyString}
      {PAGINATION_MESSAGES.VISIBLE_RANGE_PREFIX}{" "}
      <b>
        {(props.activePageNumber - PAGINATION_INDEXING.PAGE_INDEX_OFFSET) * props.size +
          PAGINATION_INDEXING.PAGE_INDEX_OFFSET} -{" "}
        {(props.activePageNumber - PAGINATION_INDEXING.PAGE_INDEX_OFFSET) * props.size +
          props.rowsPerPage}
      </b>{" "}
      {PAGINATION_MESSAGES.TOTAL_ENTRIES_PREFIX} <b>{props.totalRows.toLocaleString()}</b>{" "}
      {PAGINATION_MESSAGES.ENTRIES_LABEL}
    </small>
  );

  const shouldRenderItem = (item) => {
    return (
      item.page === props.activePageNumber + PAGINATION_INDEXING.PAGE_INDEX_OFFSET ||
      item.page === props.activePageNumber - PAGINATION_INDEXING.PAGE_INDEX_OFFSET ||
      item.page === props.activePageNumber ||
      item.type === PAGINATION_ITEM_TYPES.PREVIOUS ||
      item.type === PAGINATION_ITEM_TYPES.NEXT ||
      item.type === PAGINATION_ITEM_TYPES.FIRST ||
      item.type === PAGINATION_ITEM_TYPES.LAST ||
      (props.activePageNumber === props.totalPages &&
        item.page ===
          props.activePageNumber - PAGINATION_DISPLAY.EDGE_NEIGHBOR_OFFSET) ||
      (props.activePageNumber === PAGINATION_INDEXING.FIRST_PAGE_NUMBER &&
        item.page ===
          props.activePageNumber + PAGINATION_DISPLAY.EDGE_NEIGHBOR_OFFSET)
    );
  };

  return (
    <table className="table table-hover-none overflow-hidden">
      <tfoot className="paginator-footer">
        <tr className="row pagination-row px-1">
          <td
            className={`col-sm-8 ${paginationCustomClass}`}
            style={paginatorCellStyle}
          >
            <div
              className={
                props.totalPages >= PAGINATION_INDEXING.FIRST_PAGE_NUMBER &&
                (props.totalRows > PAGINATION_PAGE_SIZES.DEFAULT ||
                  (props.isPaginationAfterFiveRecords &&
                    props.totalRows > PAGINATION_PAGE_SIZES.COMPACT))
                  ? "d-flex align-items-center"
                  : "hidden"
              }
            >
              <Pagination
                data-testid={TEST_IDS.PAGINATION}
                shape={PAGINATION_DISPLAY.CONTROL_SHAPE}
                variant={PAGINATION_DISPLAY.CONTROL_VARIANT}
                count={props.totalPages}
                onChange={(e, page) => props.activatePage(page)}
                page={props.activePageNumber}
                showFirstButton
                showLastButton
                sx={paginationSx}
                renderItem={(item) => {
                  if (shouldRenderItem(item)) {
                    if (
                      isInputVisible &&
                      item.page === props.activePageNumber &&
                      item.type === PAGINATION_ITEM_TYPES.PAGE
                    ) {
                      return (
                        <PageNumberInput
                          value={pageValue}
                          onChange={handleInputChange}
                          onKeyDown={handlePressEnter}
                          totalPages={props.totalPages}
                        />
                      );
                    }
                    return (
                      <PaginationItem
                        {...item}
                        onClick={(e) => {
                          if (
                            item.page === props.activePageNumber &&
                            item.type === PAGINATION_ITEM_TYPES.PAGE
                          ) {
                            e.stopPropagation();
                            setPageValue(item.page);
                            setIsInputVisible(true);
                          } else {
                            item.onClick(e);
                          }
                        }}
                      />
                    );
                  }
                  return null;
                }}
              />
              {props.isPaginationDropDownEnable && (
                <div className="px-3">
                  <small>
                    <Form.Select
                      data-testid={TEST_IDS.PAGE_SIZE_SELECT}
                      value={props.currentPageSize}
                      onChange={(e) => activateCurrentPageSize(e)}
                    >
                      {props.manualPageSize.map((element) => {
                        return (
                          <option key={element.value} value={element.value}>
                            {element.label}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </small>
                </div>
              )}
            </div>
          </td>
          <td
            className={`col-sm-4 d-flex justify-content-md-end justify-content-center align-items-center ${paginationCustomClass}`}
            style={paginatorCellStyle}
          >
            {renderPageInfo()}
          </td>
        </tr>
      </tfoot>
    </table>
  );
};
